/**
 * SF-03 Perfection index (INV-CH-SF-03, ENGINE-SPEC §4; changes C-D7-SF03, C-SF03-RES).
 *
 * (a) Keystroke noise per 1,000 prose words: space before , or ., the typos dont/cant/wont/im/…,
 *     doubled !? and a lone lowercase "i". Two-way, descending ramp `[[0,58],[1,30],[3,18],[8,8]]`
 *     (the seed's raw-count ramp, D7, is re-expressed as a rate; the zero-noise value 58 is the
 *     seed's own long-text value).
 * (b) Double space after a full stop: export residue, a machine indicator. It is a SHARE of the
 *     sentence boundaries, not a count, and adds +22 (capped at 92) when the share is ≥ 0.3 and
 *     there are at least 3 of them. It is observable only where whitespace is preserved (txt, md,
 *     paste); elsewhere the channel abstains on this feature and says so in a note.
 * (c) Duplicated words: evidence only (paste or edit artefact, direction ambiguous).
 *
 * Noise is the only weighted event; the residue bump is applied to the display score after the
 * shared estimator (features.bump = 1).
 */
import { REGISTER_PROSE } from '../document/types.ts';
import { loadEnglishLexicons } from '../lang/en/lexicons/index.ts';
import { type CompiledEnglish, compileEnglishLexicons } from '../lexicons/index.ts';
import { clamp } from '../util/stats.ts';
import { derivePrior, makeEvidence, rateChannel } from './framework.ts';
import { fmt, proseParagraphs, proseSentenceList } from './helpers.ts';
import type {
	ChannelDef,
	ChannelImpl,
	ChannelResult,
	Measurement,
	Ramp,
	RunContext,
} from './types.ts';

const RAMP: Ramp = [
	[0, 58],
	[1, 30],
	[3, 18],
	[8, 8],
];

const F_SPACE_PUNCT = 1;
const F_TYPO = 2;
const F_REPEAT_PUNCT = 3;
const F_LONE_I = 4;
const F_DOUBLE_SPACE = 5;
const F_DUPLICATE = 6;

const BUMP = 22;
const BUMP_CAP = 92;
const BUMP_MIN_SHARE = 0.3;
const BUMP_MIN_COUNT = 3;

export const sf03Def: ChannelDef = {
	id: 'SF-03',
	name: 'Perfection index',
	family: 'TEXTURE',
	axis: 'authorship',
	registers: ['prose'],
	direction: -1,
	oneWay: false,
	estimator: 'rate',
	features: [
		{ id: 'noise', label: 'Keystroke noise per 1,000 words' },
		{ id: 'spaceBeforePunct', label: 'Space before comma or full stop' },
		{ id: 'typo', label: 'Dropped-apostrophe typos (dont, cant, im …)' },
		{ id: 'repeatedPunct', label: 'Repeated !/?' },
		{ id: 'loneLowercaseI', label: 'Lone lowercase "i"' },
		{ id: 'doubleSpace', label: 'Double space after a full stop (share of sentence boundaries)' },
		{ id: 'duplicatedWord', label: 'Duplicated word (evidence only)' },
	],
	minExposure: 500,
	exposureUnit: 'words',
	lang: 'native',
	ramp: RAMP,
	unitScale: 1000,
	unitLabel: 'per 1k words',
	prior: derivePrior(RAMP, 1000, 'words', 1.5),
	weight: 0.5,
	nd: 1,
	changes: ['C-D7-SF03', 'C-SF03-RES'],
};

let lexCache: CompiledEnglish | undefined;
const lex = (): CompiledEnglish => {
	lexCache ??= compileEnglishLexicons(loadEnglishLexicons());
	return lexCache;
};

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const nr = lex().channels['ch-sf03'];
	const reSpacePunct = nr.get('spaceBeforePeriodComma');
	const reTypo = nr.get('keystrokeTypos');
	const reRepeat = nr.get('repeatedPunct');
	const reLoneI = nr.get('loneLowercaseI');
	const reDouble = nr.get('doubleSpaceAfterStop');
	const reDup = nr.get('duplicatedWord');
	const whitespaceObservable = doc.observability.whitespace;
	const P = doc.paragraphs.count;
	const k = new Float64Array(P);
	const e = new Float64Array(P);
	const ev = makeEvidence();
	let noise = 0;
	let residue = 0;
	let dup = 0;

	for (const p of proseParagraphs(doc)) {
		if (doc.paragraphs.register[p] !== REGISTER_PROSE) continue;
		e[p] = doc.paragraphs.ownWords[p] as number;
		const b = doc.blocks[doc.paragraphs.block[p] as number];
		if (!b) continue;
		const text = doc.text.slice(b.charStart, b.charEnd);
		const base = b.charStart;
		let kp = 0;
		const each = (
			re: RegExp,
			f: (m: RegExpExecArray) => [number, number] | null,
			feature: number,
			weight: number
		): void => {
			re.lastIndex = 0;
			for (let m = re.exec(text); m; m = re.exec(text)) {
				const r = f(m);
				if (r) {
					ev.add(base + r[0], base + r[1], feature, weight);
					if (weight > 0) {
						kp += weight;
						noise += weight;
					}
				}
				if (m[0].length === 0) re.lastIndex++;
			}
			re.lastIndex = 0;
		};
		each(reSpacePunct, (m) => [m.index, m.index + m[0].length], F_SPACE_PUNCT, 1);
		each(reTypo, (m) => [m.index, m.index + m[0].length], F_TYPO, 1);
		each(reRepeat, (m) => [m.index, m.index + m[0].length], F_REPEAT_PUNCT, 1);
		each(
			reLoneI,
			(m) => {
				const at = m.index + m[0].indexOf('i');
				return [at, at + 1];
			},
			F_LONE_I,
			1
		);
		if (whitespaceObservable) {
			reDouble.lastIndex = 0;
			for (let m = reDouble.exec(text); m; m = reDouble.exec(text)) {
				ev.add(base + m.index, base + m.index + m[0].length, F_DOUBLE_SPACE, 0);
				residue++;
			}
			reDouble.lastIndex = 0;
		}
		reDup.lastIndex = 0;
		for (let m = reDup.exec(text); m; m = reDup.exec(text)) {
			ev.add(base + m.index, base + m.index + m[0].length, F_DUPLICATE, 0);
			dup++;
		}
		reDup.lastIndex = 0;
		k[p] = kp;
	}

	// the double-space share is taken over the prose sentence count (the conservative upper bound
	// of the number of sentence boundaries)
	const boundaries = proseSentenceList(doc).count;
	const share = boundaries > 0 ? residue / boundaries : 0;
	const bump = whitespaceObservable && share >= BUMP_MIN_SHARE && residue >= BUMP_MIN_COUNT ? 1 : 0;
	const words = doc.proseWords;
	const rate = words > 0 ? (noise / words) * 1000 : 0;
	const summary: string[] = [];
	if (noise > 0)
		summary.push(`${noise} keystroke artefact${noise === 1 ? '' : 's'}: human typing noise`);
	if (bump)
		summary.push(
			`${residue}× double space after full stop: uniform export / stripped-citation residue, machine indicator`
		);
	if (dup > 0)
		summary.push(
			`${dup} duplicated word${dup === 1 ? '' : 's'}: paste or edit artefact, direction ambiguous`
		);
	summary.push(
		`${fmt(rate, 2)} noise/1000w · ${residue} double-space (${fmt(share * 100, 0)}% of boundaries)`
	);
	const notes: string[] = [];
	if (!whitespaceObservable)
		notes.push('double-space residue not observable in this format (whitespace not preserved)');
	return {
		k,
		e,
		evidence: ev,
		notes,
		features: {
			noise,
			doubleSpaceCount: residue,
			doubleSpaceShare: share,
			bump,
			duplicatedWords: dup,
		},
		summary,
	};
}

const base: ChannelImpl = rateChannel(sf03Def, measure);

/** The +22 export-residue bump acts on the display score (and its interval), capped at 92. */
export const sf03: ChannelImpl = {
	def: base.def,
	phase: base.phase,
	run(ctx: RunContext): ChannelResult {
		const r = base.run(ctx);
		if (r.status === 'scored' && r.features.bump === 1) {
			const up = (x: number) => clamp(x + BUMP, 0, BUMP_CAP);
			r.doc.score = up(r.doc.score);
			r.doc.scoreCi90 = [up(r.doc.scoreCi90[0]), up(r.doc.scoreCi90[1])];
		}
		return r;
	},
};
export { sf03Def as def, sf03 as impl };
